import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop'
import { CommonModule } from '@angular/common'
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MatAutocompleteModule } from '@angular/material/autocomplete'
import { MatButtonModule } from '@angular/material/button'
import { MatCardModule } from '@angular/material/card'
import { MatChipsModule } from '@angular/material/chips'
import { MatDialog } from '@angular/material/dialog'
import { MatDividerModule } from '@angular/material/divider'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatIconModule } from '@angular/material/icon'
import { MatInputModule } from '@angular/material/input'
import { MatMenuModule } from '@angular/material/menu'
import { MatTooltipModule } from '@angular/material/tooltip'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { AgendaItemSubCategory, CallResource, MinutesAnonymization, MinutesVerboseness } from '../../calls.service'
import { TimestampPipe } from '../../timestamp.pipe'
import { convertTextToProseMirrorJson } from '../../utils'
import { PROCEDURAL_ITEM_EXAMPLES } from '../procedural-item-examples'
import {
  FileUploadRequest as ContentBlockFileUploadRequest,
  PdfPreviewRequest as ContentBlockPdfPreviewRequest,
  EMPTY_PROSEMIRROR_DOC,
  TemplateItemContentBlockComponent,
} from '../template-item-content-block/template-item-content-block.component'
import { TemplateItemCreateEditModal } from '../template-item-create-edit-modal/template-item-create-edit-modal'
import {
  CompletedTemplateContentBlockSource,
  FlattenedTemplateItem,
  MinuteTimelineSegment,
  TemplateContentBlock,
  TemplateContentBlockSourceType,
  TemplateContentBlockType,
} from '../types/template.types'

export type FileUploadRequest = ContentBlockFileUploadRequest
export type PdfPreviewRequest = ContentBlockPdfPreviewRequest

const TIMELINE_SEGMENT_MIN_WIDTH_PERCENT = 4

@Component({
  selector: 'app-template-item',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatChipsModule,
    MatDividerModule,
    MatFormFieldModule,
    MatInputModule,
    MatMenuModule,
    MatTooltipModule,
    MatAutocompleteModule,
    DragDropModule,
    TranslateModule,
    TemplateItemContentBlockComponent,
  ],
  templateUrl: './template-item.component.html',
  styleUrls: ['./template-item.component.scss'],
  providers: [TimestampPipe],
})
export class TemplateItemComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input({ required: true })
  item!: FlattenedTemplateItem
  @Input({ required: true })
  availableResources!: CallResource[]
  @Input({ required: true })
  selectedItemId!: number | null
  @Input()
  attendeeNames: string[] = []
  @Input()
  allowFileUpload = true
  @Input()
  superModeEnabled = false
  @Input()
  readonly = false
  @Input({ required: true })
  defaultLevelOfDetail!: MinutesVerboseness
  @Input({ required: true })
  defaultAnonymization!: MinutesAnonymization

  @Output()
  itemSelected = new EventEmitter<number>()
  @Output()
  contentChanged = new EventEmitter<void>()
  @Output()
  fileUploadRequested = new EventEmitter<FileUploadRequest>()
  @Output()
  pdfPreviewRequested = new EventEmitter<PdfPreviewRequest>()

  @ViewChild('titleInput')
  titleInput?: ElementRef<HTMLInputElement>

  /**
   * Marker element for the container of the card that we use to observe when the card header should become sticky.
   */
  @ViewChild('cardMarkerRef')
  cardMarkerRef!: ElementRef<HTMLSpanElement>

  /**
   * Marker element for the header of the card that we use to observe when the card header should become sticky.
   */
  @ViewChild('headerMarkerRef')
  headerMarkerRef!: ElementRef<HTMLSpanElement>

  changeDetectorRef = inject(ChangeDetectorRef)

  dialog = inject(MatDialog)
  translateService = inject(TranslateService)
  timestampPipe = inject(TimestampPipe)

  TemplateContentBlockType = TemplateContentBlockType
  AgendaItemSubCategory = AgendaItemSubCategory

  binarySubCategories: AgendaItemSubCategory[] = [
    AgendaItemSubCategory.CHAIR_OF_THE_MEETING,
    AgendaItemSubCategory.NOTICE_GIVEN_TO_DIRECTORS,
    AgendaItemSubCategory.QUORUM,
    AgendaItemSubCategory.APOLOGIES_FOR_ABSENCE,
    AgendaItemSubCategory.DECLARATIONS_OF_INTERESTS,
    AgendaItemSubCategory.APPROVAL_OF_PREVIOUS_BOARD_MINUTES,
    AgendaItemSubCategory.ANY_OTHER_BUSINESS,
    AgendaItemSubCategory.DATE_OF_NEXT_MEETING,
    AgendaItemSubCategory.CLOSE_OF_MEETING,
  ]

  newPresenterName = ''
  filteredAttendeeNames: string[] = []
  isEditingTitle = false
  temporaryTitle = ''
  blockIdToFocus: number | null = null

  cardMarkerObserver: IntersectionObserver | null = null
  cardMarkerVisible: boolean = false
  headerMarkerObserver: IntersectionObserver | null = null
  headerMarkerVisible: boolean = false

  private nextTemporaryId = -100000

  private getNextTemporaryId(): number {
    return this.nextTemporaryId--
  }

  ngAfterViewInit(): void {
    this.cardMarkerObserver = new IntersectionObserver(
      (entries) => {
        const anyIntersecting = entries.some((entry) => entry.isIntersecting)
        this.cardMarkerVisible = anyIntersecting
        this.changeDetectorRef.detectChanges()
      },
      { root: null, threshold: 0.0 }
    )

    this.headerMarkerObserver = new IntersectionObserver(
      (entries) => {
        const anyIntersecting = entries.some((entry) => entry.isIntersecting)
        this.headerMarkerVisible = anyIntersecting
        this.changeDetectorRef.detectChanges()
      },
      { root: null, threshold: 0.0 }
    )

    this.cardMarkerObserver.observe(this.cardMarkerRef.nativeElement)
    this.headerMarkerObserver.observe(this.headerMarkerRef.nativeElement)
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedItemId']) {
      if (this.isEditingTitle && this.selectedItemId !== this.item.id) {
        this.onTitleFocusLoss()
      }
    }
    if (changes['attendeeNames']) {
      this.filteredAttendeeNames = [...this.attendeeNames]
    }
  }

  ngOnDestroy(): void {
    this.cardMarkerObserver?.disconnect()
    this.headerMarkerObserver?.disconnect()
  }

  isHeaderSticky(): boolean {
    return this.item.expanded && this.cardMarkerVisible && !this.headerMarkerVisible
  }

  filterAttendeeNames(): void {
    const filterValue = this.newPresenterName.toLowerCase()
    this.filteredAttendeeNames = this.attendeeNames
      .filter((name) => !this.item.presenters.includes(name))
      .filter((name) => name.toLowerCase().includes(filterValue))
  }

  selectItem(itemId: number) {
    this.itemSelected.emit(itemId)
  }

  toggleExpanded() {
    this.item.expanded = !this.item.expanded
  }

  onTitleClick() {
    if (this.selectedItemId === this.item.id) {
      this.startEditingTitle()
    } else {
      this.selectItem(this.item.id)
    }
  }

  startEditingTitle() {
    this.isEditingTitle = true
    this.temporaryTitle = this.item.title
    setTimeout(() => {
      this.titleInput?.nativeElement.focus()
    }, 0)
  }

  saveTitleEdit() {
    const trimmedTitle = this.temporaryTitle.trim()
    if (trimmedTitle !== '' && trimmedTitle !== this.item.title) {
      this.item.title = trimmedTitle
      this.markAsChanged()
    }
    this.isEditingTitle = false
    this.temporaryTitle = ''
  }

  cancelTitleEdit() {
    this.isEditingTitle = false
    this.temporaryTitle = ''
  }

  onTitleFocusLoss() {
    if (this.temporaryTitle.trim() === '' || this.temporaryTitle === this.item.title) {
      this.cancelTitleEdit()
    } else {
      this.saveTitleEdit()
    }
  }

  onTitleKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault()
      this.saveTitleEdit()
    } else if (event.key === 'Escape') {
      event.preventDefault()
      this.cancelTitleEdit()
    }
  }

  editItem() {
    const dialogRef = this.dialog.open(TemplateItemCreateEditModal, {
      data: { item: this.item, isCreating: false, attendeeNames: this.attendeeNames },
      width: '500px',
    })

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.item.title = result.title
        this.item.duration = result.duration
        this.item.presenters = result.presenters
        this.markAsChanged()
      }
    })
  }

  markAsChanged() {
    this.contentChanged.emit()
  }

  onBlockDrop(event: CdkDragDrop<TemplateContentBlock[]>) {
    if (event.previousIndex === event.currentIndex) {
      return
    }
    moveItemInArray(this.item.contentBlocks, event.previousIndex, event.currentIndex)
    this.item.contentBlocks = this.item.contentBlocks.map((block, index) => ({ ...block, index }))
    this.markAsChanged()
  }

  addContentBlock(blockType: TemplateContentBlockType, index: number, category: AgendaItemSubCategory | null) {
    const newBlockId = this.getNextTemporaryId()
    const baseBlock = {
      id: newBlockId,
      templateItemId: this.item.id,
      index,
      sources: [],
      levelOfDetail: this.defaultLevelOfDetail,
      anonymization: this.defaultAnonymization,
      category,
    }

    const transcriptSource: CompletedTemplateContentBlockSource = {
      type: TemplateContentBlockSourceType.TRANSCRIPT,
      callResourceId: null,
      callResource: null,
      templateContentBlockId: newBlockId,
      isPending: false,
    }

    const newBlock: TemplateContentBlock = (() => {
      switch (blockType) {
        case TemplateContentBlockType.FIXED_TEXT:
          return {
            ...baseBlock,
            type: TemplateContentBlockType.FIXED_TEXT,
            data: { formattedTextBlock: { content: EMPTY_PROSEMIRROR_DOC, index: 0 } },
          }
        case TemplateContentBlockType.VARIABLE_TEXT:
          return {
            ...baseBlock,
            type: TemplateContentBlockType.VARIABLE_TEXT,
            data: { formattedTextBlock: { content: EMPTY_PROSEMIRROR_DOC, index: 0 } },
          }
        case TemplateContentBlockType.DISCUSSION_SUMMARY:
          return {
            ...baseBlock,
            type: TemplateContentBlockType.DISCUSSION_SUMMARY,
            sources: [transcriptSource],
            data: { guidance: '' },
          }
        case TemplateContentBlockType.KEY_MESSAGES:
          return {
            ...baseBlock,
            type: TemplateContentBlockType.KEY_MESSAGES,
            data: { guidance: '' },
          }
        case TemplateContentBlockType.DISCUSSION_CHALLENGE:
          return {
            ...baseBlock,
            type: TemplateContentBlockType.DISCUSSION_CHALLENGE,
            data: { guidance: '' },
          }
        case TemplateContentBlockType.MATTERS_ARISING:
          return {
            ...baseBlock,
            type: TemplateContentBlockType.MATTERS_ARISING,
            sources: [transcriptSource],
            data: { guidance: '' },
          }
        case TemplateContentBlockType.BINARY: {
          const examples = category !== null ? (PROCEDURAL_ITEM_EXAMPLES[category] ?? null) : null
          const formattedTextBlocks =
            examples !== null
              ? examples.map((text, textIndex) => ({
                  content: convertTextToProseMirrorJson(text),
                  index: textIndex,
                }))
              : [
                  { content: EMPTY_PROSEMIRROR_DOC, index: 0 },
                  { content: EMPTY_PROSEMIRROR_DOC, index: 1 },
                ]
          return {
            ...baseBlock,
            type: TemplateContentBlockType.BINARY,
            sources: [transcriptSource],
            data: { formattedTextBlocks },
          }
        }
      }
    })()

    this.item.contentBlocks.splice(index, 0, newBlock)
    this.item.contentBlocks = this.item.contentBlocks.map((block, blockIndex) => ({ ...block, index: blockIndex }))

    if (
      blockType === TemplateContentBlockType.DISCUSSION_SUMMARY ||
      blockType === TemplateContentBlockType.KEY_MESSAGES ||
      blockType === TemplateContentBlockType.DISCUSSION_CHALLENGE ||
      blockType === TemplateContentBlockType.MATTERS_ARISING
    ) {
      this.blockIdToFocus = newBlockId
      setTimeout(() => {
        this.blockIdToFocus = null
      }, 100)
    }
    this.itemSelected.emit(this.item.id)
    this.markAsChanged()
  }

  removeContentBlock(blockId: number) {
    this.item.contentBlocks = this.item.contentBlocks.filter((block) => block.id !== blockId).map((block, index) => ({ ...block, index }))
    this.markAsChanged()
  }

  addPresenter(presenterName: string) {
    if (presenterName.trim() === '') {
      return
    }
    this.item.presenters.push(presenterName.trim())
    this.newPresenterName = ''
    this.markAsChanged()
  }

  removePresenter(presenterIndex: number) {
    this.item.presenters.splice(presenterIndex, 1)
    this.markAsChanged()
  }

  onFileUploadRequested(request: FileUploadRequest) {
    this.fileUploadRequested.emit(request)
  }

  onPdfPreviewRequested(request: PdfPreviewRequest) {
    this.pdfPreviewRequested.emit(request)
  }

  get hasAttachments(): boolean {
    return this.item.contentBlocks.some((block) =>
      block.sources.some((source) => !source.isPending && source.type === TemplateContentBlockSourceType.FILE_UPLOAD)
    )
  }

  get numberOfBlocks(): number {
    return this.item.contentBlocks.length
  }

  get blocksTooltip(): string {
    const count = this.numberOfBlocks
    if (count === 0) {
      return this.translateService.instant('TEMPLATES.EDITOR.NO_BLOCKS_TOOLTIP')
    }
    if (count === 1) {
      return `1 ${this.translateService.instant('TEMPLATES.EDITOR.CONTENT_BLOCK_SINGULAR')}`
    }
    return `${count} ${this.translateService.instant('TEMPLATES.EDITOR.CONTENT_BLOCK_PLURAL')}`
  }

  get numberOfAttachments(): number {
    const resourceIds = this.item.contentBlocks
      .flatMap((block) => block.sources)
      .filter((source) => !source.isPending && source.type === TemplateContentBlockSourceType.FILE_UPLOAD && source.callResourceId !== null)
      .map((source) => source.callResourceId!)
    return new Set(resourceIds).size
  }

  get attachmentsTooltip(): string {
    const count = this.numberOfAttachments
    if (count === 1) {
      return `1 ${this.translateService.instant('TEMPLATES.EDITOR.ATTACHMENT_SINGULAR')}`
    }
    return `${count} ${this.translateService.instant('TEMPLATES.EDITOR.ATTACHMENT_PLURAL')}`
  }

  get shouldShowTimelineBar(): boolean {
    return this.item.hasMinuteContent && this.item.contentBlocks.length > 0
  }

  get hasDiscussionSummaryBlock(): boolean {
    return this.item.contentBlocks.some((block) => block.type === TemplateContentBlockType.DISCUSSION_SUMMARY)
  }

  get hasMattersArisingBlock(): boolean {
    return this.item.contentBlocks.some((block) => block.type === TemplateContentBlockType.MATTERS_ARISING)
  }

  get timelineTooltip(): string {
    return this.item.minuteTimelineSegments
      .map((segment, index) => {
        const start = this.timestampPipe.transform(segment.startSeconds)
        const end = this.timestampPipe.transform(segment.endSeconds)
        return `${this.translateService.instant('TEMPLATES.EDITOR.DISCUSSION_SEGMENT')} ${index + 1}: ${start} - ${end}`
      })
      .join('\n')
  }

  getSegmentStyle(segment: MinuteTimelineSegment): { left: string; width: string } {
    const minWidth = TIMELINE_SEGMENT_MIN_WIDTH_PERCENT
    // Ensure left position allows the full min width to be visible
    const left = Math.min(segment.startPercent, 100 - minWidth)
    // Calculate width, ensuring minimum but not overflowing
    const width = Math.min(Math.max(segment.widthPercent, minWidth), 100 - left)
    return {
      left: `${left}%`,
      width: `${width}%`,
    }
  }
}
